import { ApplicationConfig, APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { provideRouter, TitleStrategy, withViewTransitions } from '@angular/router';
import { storefrontViewTransition } from '@coreshop/feature-storefront/utils/storefront-view-transition';
import { LocalizedTitleStrategy } from './localized-title.strategy';
import { provideHttpClient, withInterceptors, withFetch, HttpContext } from '@angular/common/http';
import { provideZoneChangeDetection } from '@angular/core';
import { registerLocaleData, PRECONNECT_CHECK_BLOCKLIST } from '@angular/common';
import localeEsPe from '@angular/common/locales/es-PE';
import localeEnUs from '@angular/common/locales/en';
import localePtBr from '@angular/common/locales/pt';
import { routes } from './app.routes';
import { catchError, EMPTY, tap } from 'rxjs';
import { AppConfig } from '@env/app-config';

// i18n: traducciones en runtime con Transloco
import { provideTransloco } from '@jsverse/transloco';
import { TranslocoHttpLoader } from './transloco-loader';
import { LanguageService, LANG_STORAGE_KEY } from '@shared/services/usuario/language.service';

// Zona horaria: resuelve y propaga la zona del usuario para el formateo de fechas
import { TimezoneService } from '@shared/services/usuario/timezone.service';

// Locale data para DatePipe/number/currency y el pipe appDate
registerLocaleData(localeEsPe);
registerLocaleData(localeEnUs);
registerLocaleData(localePtBr);

/** Mapea el código de idioma de la app (ISO 639-1) al locale de formato de Angular. */
const LANG_TO_LOCALE: Record<string, string> = { es: 'es-PE', en: 'en-US', pt: 'pt-BR' };

/**
 * LOCALE_ID inicial para los pipes nativos de Angular (fecha/número/moneda).
 * Se resuelve al arranque desde el último idioma recordado en localStorage.
 */
function resolveInitialLocaleId(): string {
  try {
    const lang = localStorage.getItem(LANG_STORAGE_KEY);
    if (lang && LANG_TO_LOCALE[lang]) {
      return LANG_TO_LOCALE[lang];
    }
  } catch {
    /* localStorage no disponible — usar default */
  }
  return 'es-PE';
}

// Interceptores modernos (funcionales)
import { authInterceptor } from '@interceptors/auth.interceptor';
import { errorInterceptor, SUPPRESS_HTTP_ERRORS } from '@interceptors/error.interceptor';
import { moduleContextInterceptor } from '@interceptors/module-context.interceptor';
import { companyContextInterceptor } from '@interceptors/company-context.interceptor';
import { languageInterceptor } from '@interceptors/language.interceptor';

// Inicializando AG Grid con keywords en español
import { provideGlobalGridOptions } from 'ag-grid-community';
import { localeEs } from '@shared/constants/translate-ag-grid.constant';

// Session recovery
import { AuthService } from '@shared/services/auth.service';
import { AuthApiService } from '@shared/services/auth-api.service';

provideGlobalGridOptions({
  localeText: localeEs
});

/** Rutas públicas de tienda: no deben recuperar ni cerrar la sesión admin. */
function isStorefrontBootPath(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/store/');
}

/**
 * APP_INITIALIZER: recupera el access token vía /auth/refresh-token (cookie HttpOnly `rt`).
 * En `/store/...` no se intenta: la vitrina es pública y un refresh fallido no debe
 * disparar «Sesión expirada» ni redirigir a `/auth/login`.
 */
function initializeSession(authService: AuthService, authApi: AuthApiService): () => Promise<void> {
  return () => {
    if (isStorefrontBootPath()) {
      return Promise.resolve();
    }

    if (!authService.hasStoredUser()) {
      return Promise.resolve();
    }

    const suppressCtx = new HttpContext().set(SUPPRESS_HTTP_ERRORS, true);
    return authApi.refreshToken(suppressCtx).pipe(
      tap((res: any) => authService.recoverSession(res.user)),
      catchError(() => {
        authService.clearLocalSession();
        return EMPTY;
      })
    ).toPromise().then(() => {});
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withViewTransitions({ skipInitialTransition: true, onViewTransitionCreated: storefrontViewTransition })),
    { provide: TitleStrategy, useClass: LocalizedTitleStrategy },

    provideHttpClient(
      withFetch(),
      withInterceptors([
        languageInterceptor,
        authInterceptor,
        moduleContextInterceptor,
        companyContextInterceptor,
        errorInterceptor,
      ])
    ),

    provideTransloco({
      config: {
        availableLangs: ['es', 'en', 'pt'],
        defaultLang: 'es',
        fallbackLang: 'es',
        reRenderOnLangChange: true,
        prodMode: AppConfig.isProduction,
        missingHandler: { useFallbackTranslation: true },
      },
      loader: TranslocoHttpLoader,
    }),

    {
      provide: APP_INITIALIZER,
      useFactory: initializeSession,
      deps: [AuthService, AuthApiService],
      multi: true,
    },

    { provide: LOCALE_ID, useFactory: resolveInitialLocaleId },

    {
      provide: PRECONNECT_CHECK_BLOCKLIST,
      useValue: typeof window !== 'undefined' ? [window.location.origin] : [],
      multi: true,
    },

    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [TimezoneService],
      multi: true,
    },

    {
      provide: APP_INITIALIZER,
      useFactory: (lang: LanguageService) => () => lang.init(),
      deps: [LanguageService],
      multi: true,
    },
  ],
};
